import { SalesforceNamespace } from 'datasource-service-lib';
import { AutoGenIconPipe } from 'pipes-lib';
import { ISimpleTableColumn, ISimpleTableRow } from '../../../../../common-components/simple-table/simple-table.interfaces';
import { IAttachmentMergeFile } from 'interfaces-lib';

export interface IDatasourceColumnConfig {
  columns: ISimpleTableColumn[];
  cellBuilder: (args: IDatasourceCellBuilderArgs) => ISimpleTableRow['cells'];
}

export interface IDatasourceCellBuilderArgs {
  file: IAttachmentMergeFile;
  formatSize: (size?: number) => string;
  formatDate: (date?: number) => string;
}

const DatasourceColumnSorts = {
  byName: (a: IAttachmentMergeFile, b: IAttachmentMergeFile) => a.name.localeCompare(b.name),
  byType: (a: IAttachmentMergeFile, b: IAttachmentMergeFile) => a.type.localeCompare(b.type),
  bySize: (a: IAttachmentMergeFile, b: IAttachmentMergeFile) => (a.size ?? 0) - (b.size ?? 0),
  byModified: (a: IAttachmentMergeFile, b: IAttachmentMergeFile) => (a.dateModified ?? 0) - (b.dateModified ?? 0),
};

export enum DatasourceTableIdentifier {
  AttachFile = 'attach-file-table',
}

interface IDatasourceColumnProvider {
  getConfig(table: DatasourceTableIdentifier, renderMode: string): IDatasourceColumnConfig;
}

class DefaultDatasourceColumnProvider implements IDatasourceColumnProvider {
  getConfig(table: DatasourceTableIdentifier, renderMode: string): IDatasourceColumnConfig {
    switch (table) {
      case DatasourceTableIdentifier.AttachFile:
        return this.getAttachFileConfig(renderMode);
      default:
        throw new Error(`No datasource column config registered for table '${table}'.`);
    }
  }

  protected getAttachFileConfig(renderMode: string): IDatasourceColumnConfig {
    if (renderMode !== 'web-app') {
      return {
        columns: [
          {
            title: 'Name',
            width: '50%',
            sortFunction: DatasourceColumnSorts.byName,
          },
          {
            title: 'Date Added',
            width: '50%',
            sortFunction: DatasourceColumnSorts.byModified,
          },
        ],
        cellBuilder: ({ file, formatDate }) => [
          { autoIcon: AutoGenIconPipe.AUTO_GEN_ICON, text: file.name, fileExtension: file.fileExtension },
          { text: formatDate(file.dateModified) },
        ],
      };
    }

    return {
      columns: [
        {
          title: 'Name',
          width: '45%',
          sortFunction: DatasourceColumnSorts.byName,
        },
        {
          title: 'Type',
          width: '20%',
          sortFunction: DatasourceColumnSorts.byType,
        },
        {
          title: 'Size',
          width: '10%',
          sortFunction: DatasourceColumnSorts.bySize,
        },
        {
          title: 'Date Added',
          width: '25%',
          sortFunction: DatasourceColumnSorts.byModified,
        },
      ],
      cellBuilder: ({ file, formatSize, formatDate }) => [
        { autoIcon: AutoGenIconPipe.AUTO_GEN_ICON, text: file.name, fileExtension: file.fileExtension },
        { text: file.type },
        { text: formatSize(file.size) },
        { text: formatDate(file.dateModified) },
      ],
    };
  }
}

class SalesforceDatasourceColumnProvider extends DefaultDatasourceColumnProvider {
  protected override getAttachFileConfig(renderMode: string): IDatasourceColumnConfig {
    if (renderMode !== 'web-app') {
      return super.getAttachFileConfig(renderMode);
    }

    return {
      columns: [
        {
          title: 'Name',
          width: '55%',
          sortFunction: DatasourceColumnSorts.byName,
        },
        {
          title: 'Size',
          width: '15%',
          sortFunction: DatasourceColumnSorts.bySize,
        },
        {
          title: 'Date Added',
          width: '30%',
          sortFunction: DatasourceColumnSorts.byModified,
        },
      ],
      cellBuilder: ({ file, formatSize, formatDate }) => [
        { autoIcon: AutoGenIconPipe.AUTO_GEN_ICON, text: file.name, fileExtension: file.fileExtension },
        { text: formatSize(file.size) },
        { text: formatDate(file.dateModified) },
      ],
    };
  }
}

const providers: Record<string, IDatasourceColumnProvider> = {
  default: new DefaultDatasourceColumnProvider(),
  [SalesforceNamespace.name]: new SalesforceDatasourceColumnProvider(),
};

export class DatasourceColumnProviderFactory {
  static getConfig(namespace: string | undefined, renderMode: string, table: DatasourceTableIdentifier): IDatasourceColumnConfig {
    const provider = (namespace && providers[namespace]) ?? providers.default;
    return provider.getConfig(table, renderMode);
  }
}
